/**
 * Cleans HTML content by removing citations and interactive elements
 * for cleaner copying to clipboard.
 *
 * @param element - The HTML element to clean
 * @returns Object containing cleaned HTML and plain text
 */
export function cleanContentForCopy(element: HTMLElement): {
  html: string;
  plainText: string;
} {
  const clonedContent = element.cloneNode(true) as HTMLElement;

  const citationElements = clonedContent.querySelectorAll("[data-citation], cite, [role='citation']");
  citationElements.forEach((el) => el.remove());

  const buttonElements = clonedContent.querySelectorAll("button");
  buttonElements.forEach((el) => el.remove());

  // Extract plain text while preserving line breaks from block elements
  // Replace <br> tags with newlines first
  const brElements = clonedContent.querySelectorAll("br");
  brElements.forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  // Function to extract text from a node, preserving list structure
  const extractTextFromNode = (node: Node, depth = 0): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    // Handle lists - extract all list items with proper formatting
    if (tagName === "ul" || tagName === "ol") {
      const items = Array.from(element.children)
        .filter((el) => el.tagName.toLowerCase() === "li")
        .map((li) => {
          const listText = Array.from(li.childNodes)
            .map((child) => extractTextFromNode(child, depth + 1))
            .join("")
            .trim();
          const bullet = tagName === "ol" ? "" : "• ";
          return `${bullet}${listText}`;
        })
        .filter((text) => text.trim().length > 0);
      return items.join("\n");
    }

    // Handle list items - but only if not inside a ul/ol (shouldn't happen, but safe)
    if (tagName === "li") {
      const listText = Array.from(element.childNodes)
        .map((child) => extractTextFromNode(child, depth + 1))
        .join("")
        .trim();
      return `• ${listText}`;
    }

    // Handle block elements
    if (["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre"].includes(tagName)) {
      const content = Array.from(element.childNodes)
        .map((child) => extractTextFromNode(child, depth + 1))
        .join("")
        .trim();
      return content;
    }

    // For other elements, recursively extract text
    return Array.from(element.childNodes)
      .map((child) => extractTextFromNode(child, depth + 1))
      .join("");
  };

  // Extract text from all top-level children
  const textParts = Array.from(clonedContent.children)
    .map((child) => {
      return extractTextFromNode(child);
    })
    .filter((text) => text.trim().length > 0);

  const plainText = textParts.length > 0 ? textParts.join("\n") : clonedContent.textContent || "";

  return {
    html: clonedContent.innerHTML,
    plainText: plainText.trim(),
  };
}

/**
 * Copies rich text content to clipboard, preserving formatting but removing citations.
 *
 * @param element - The HTML element to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyRichTextToClipboard(element: HTMLElement): Promise<boolean> {
  try {
    const { html, plainText } = cleanContentForCopy(element);

    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([plainText], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      }),
    ]);

    return true;
  } catch (err) {
    console.error("Failed to copy rich text:", err);
    return false;
  }
}

/**
 * Removes citation tags from markdown text.
 *
 * @param markdown - The markdown text containing citations
 * @returns Markdown text with citations removed
 */
export function stripCitationsFromMarkdown(markdown: string): string {
  return markdown.replace(/<cite>.*?<\/cite>/g, "");
}

/**
 * Copies markdown content to clipboard, removing citations.
 *
 * @param markdown - The markdown text to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyMarkdownToClipboard(markdown: string): Promise<boolean> {
  try {
    const cleanedMarkdown = stripCitationsFromMarkdown(markdown);
    await navigator.clipboard.writeText(cleanedMarkdown);
    return true;
  } catch (err) {
    console.error("Failed to copy markdown:", err);
    return false;
  }
}
